import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { can, isPublicOnly } from '../../utils/permissions';

/*
 * My email opt-outs (GET/PUT /users/me/notifications), each on until turned
 * off and saved at once. The culture pipeline's (a task given to me, work
 * starting on the stage before mine) go to whoever reads the pipeline's tasks;
 * the specimen handoffs' to whoever the server mails them to: the lab's queue
 * (`handoff:work`: requests to the owners, work given to me, a rare species
 * confirmed on what the lab holds) and the people who ask
 * (`handoff:request`: news of my own handoffs, invitations to bring a rare
 * find in); a field mission assigned to me (migration 0100) to whoever plays
 * missions (`mission:play`); a new issue report (0113) to whoever triages
 * them (`issue:manage`). A switch shows only when the server answered its key, so a
 * frontend ahead of its API offers nothing it would refuse. `wrap` puts it in
 * the account page's own section frame.
 */
const PIPELINE = [
  ['pipeline_assigned', 'assigned', 'pipeline:read'],
  ['pipeline_heads_up', 'headsUp', 'pipeline:read'],
];
const HANDOFFS = [
  ['handoff_requests', 'handoffRequests', 'handoff:work'],
  ['handoff_updates', 'handoffUpdates', 'handoff:request'],
  ['handoff_invitations', 'handoffInvitations', 'handoff:request'],
];
const MISSIONS = [
  ['mission_assignments', 'missionAssignments', 'mission:play'],
];
// A new issue report (migration 0113) mails the collection managers and
// system admins: whoever triages reports (`issue:manage`).
const ISSUES = [
  ['issue_reports', 'issueReports', 'issue:manage'],
];
const KEYS = [...PIPELINE, ...HANDOFFS, ...MISSIONS, ...ISSUES].map(([key]) => key);

export default function NotificationSettings({ user, wrap = (children) => children }) {
  const { t } = useTranslation('pipeline');
  const [prefs, setPrefs] = useState(null);
  const [state, setState] = useState('');
  const pipeline = PIPELINE.filter(([, , scope]) => can(user, scope));
  const handoffs = HANDOFFS.filter(([, , scope]) => can(user, scope));
  const missions = MISSIONS.filter(([, , scope]) => can(user, scope));
  const issues = ISSUES.filter(([, , scope]) => can(user, scope));
  // A public-only account's settings route refuses it (routes/pipeline_tasks.py),
  // though it may hand its own sighting in since public portal 2d.
  const shown = !isPublicOnly(user)
    && pipeline.length + handoffs.length + missions.length + issues.length > 0;

  useEffect(() => {
    if (!shown) return;
    api.get('/users/me/notifications')
      .then((r) => { if (KEYS.some((key) => typeof r.data?.[key] === 'boolean')) setPrefs(r.data); })
      .catch(() => setState('failed'));
  }, [shown]);

  if (!shown || !prefs) return null;
  const answered = ([key]) => typeof prefs[key] === 'boolean';
  const mine = pipeline.filter(answered);
  const theirs = handoffs.filter(answered);
  const field = missions.filter(answered);
  const reports = issues.filter(answered);
  if (mine.length + theirs.length + field.length + reports.length === 0) return null;

  // Only the key changed is sent and, on a refusal, put back: a second
  // switch flipped meanwhile keeps its own answer.
  const change = (key, value) => {
    const was = prefs[key];
    setPrefs((now) => ({ ...now, [key]: value }));
    setState('');
    api.put('/users/me/notifications', { [key]: value })
      .then((r) => {
        setPrefs((now) => ({ ...now, [key]: typeof r.data?.[key] === 'boolean' ? r.data[key] : value }));
        setState('saved');
      })
      .catch(() => { setPrefs((now) => ({ ...now, [key]: was })); setState('failed'); });
  };

  const toggle = ([key, label]) => (
    <label key={key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!prefs[key]} onChange={(e) => change(key, e.target.checked)}
        data-testid={`pipeline-notify-${key}`} style={{ marginTop: 3, width: 18, height: 18 }} />
      <span>
        <span style={{ display: 'block' }}>{t(`notifications.${label}`)}</span>
        <span style={{ display: 'block', fontSize: 13, color: 'var(--c-text-muted-fg)' }}>
          {t(`notifications.${label}Hint`)}
        </span>
      </span>
    </label>
  );

  return wrap(
    <div data-testid="pipeline-notifications">
      {mine.map(toggle)}
      {theirs.length > 0 && (
        <div role="group" aria-labelledby="handoff-notifications-heading" data-testid="handoff-notifications">
          <h3 id="handoff-notifications-heading" style={{ margin: mine.length ? '16px 0 8px' : '0 0 8px',
            fontSize: 15, color: 'var(--c-text-fg)' }}>
            {t('notifications.handoffsHeading')}
          </h3>
          {theirs.map(toggle)}
        </div>
      )}
      {field.length > 0 && (
        <div role="group" aria-labelledby="mission-notifications-heading" data-testid="mission-notifications">
          <h3 id="mission-notifications-heading" style={{ margin: mine.length + theirs.length ? '16px 0 8px' : '0 0 8px',
            fontSize: 15, color: 'var(--c-text-fg)' }}>
            {t('notifications.missionsHeading')}
          </h3>
          {field.map(toggle)}
        </div>
      )}
      {reports.length > 0 && (
        <div role="group" aria-labelledby="issue-notifications-heading" data-testid="issue-notifications">
          <h3 id="issue-notifications-heading"
            style={{ margin: mine.length + theirs.length + field.length ? '16px 0 8px' : '0 0 8px',
              fontSize: 15, color: 'var(--c-text-fg)' }}>
            {t('notifications.issuesHeading')}
          </h3>
          {reports.map(toggle)}
        </div>
      )}
      <p role="status" aria-live="polite" style={{ margin: 0, fontSize: 13,
        color: state === 'failed' ? 'var(--c-danger-fg)' : 'var(--c-text-muted-fg)' }}>
        {state === 'saved' ? t('notifications.saved') : state === 'failed' ? t('notifications.failed') : ''}
      </p>
    </div>,
  );
}
